// Theme resolution and system-preference subscription helpers.
//
// Three user-facing values are accepted: 'auto' | 'light' | 'dark'.
// Internally we always resolve to either 'light' or 'dark' before
// applying it to document.documentElement.dataset.theme.

const VALID_THEMES = ['auto', 'light', 'dark'];

export function isValidTheme(t) {
  return VALID_THEMES.includes(t);
}

function prefersDark() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

/**
 * Resolve a user-facing theme preference into the concrete value
 * to apply to the DOM: 'light' or 'dark'.
 */
export function resolveTheme(theme) {
  if (theme === 'light' || theme === 'dark') return theme;
  return prefersDark() ? 'dark' : 'light';
}

/**
 * Subscribe to system `prefers-color-scheme` changes. Returns an
 * unsubscribe function. Callers should invoke the unsubscribe in
 * their effect cleanup. Safe in non-browser/test environments.
 */
export function subscribeToSystemTheme(cb) {
  if (typeof window === 'undefined' || !window.matchMedia) {
    return () => {};
  }
  const mql = window.matchMedia('(prefers-color-scheme: dark)');
  const handler = (e) => cb(e.matches ? 'dark' : 'light');

  if (mql.addEventListener) {
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }
  // Safari < 14 fallback.
  mql.addListener(handler);
  return () => mql.removeListener(handler);
}

/**
 * Apply the resolved theme to the document root. No-op in non-browser env.
 */
export function applyTheme(theme) {
  if (typeof document === 'undefined') return;
  document.documentElement.dataset.theme = resolveTheme(theme);
}
